import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, FolderX, Home, Search, X } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { Spinner } from '@/components/ui/spinner';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils/cn';

export type BrowserNavigationDirection = 'forward' | 'back';

export interface DirectoryBrowserAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: React.ComponentProps<typeof Button>['variant'];
  /** Optional leading icon (e.g. ``<FolderOpen className="size-3.5" />``). */
  icon?: React.ReactNode;
  className?: string;
}

export interface DirectoryBrowserProps {
  title: string;
  description?: string;
  currentPath: string;
  parentPath?: string | null;
  homePath?: string | null;
  rootLabel?: string;
  rootPath?: string;
  loading?: boolean;
  error?: string | null;
  navDirection?: BrowserNavigationDirection;
  primaryAction?: DirectoryBrowserAction;
  headerActions?: React.ReactNode;
  /** Show a path-entry affordance between the back and home buttons. */
  showPathSearch?: boolean;
  /** Normalize typed paths before navigation. Defaults to trimmed input. */
  normalizePathInput?: (path: string) => string;
  /**
   * When true, hide the hero title/description block and drop the
   * tinted background on the breadcrumb strip so the toolbar reads
   * as a flush continuation of whatever chrome the parent provides
   * (tabs, app toolbar, etc.). Action slots still render.
   */
  inline?: boolean;
  onNavigate: (path: string) => void;
  onNavigateUp?: () => void;
  /**
   * Click handler for the Retry button surfaced when ``error`` is
   * set. May return a Promise; the button stays disabled (with a
   * spinner) until it resolves so a still-failing retry gives
   * unmistakable visual feedback.
   */
  onRetry?: () => void | Promise<void>;
  children?: React.ReactNode;
  className?: string;
}

export function DirectoryBrowser({
  title,
  description,
  currentPath,
  parentPath,
  homePath,
  rootLabel = 'Workspace',
  rootPath = '',
  loading = false,
  error,
  navDirection = 'forward',
  primaryAction,
  headerActions,
  showPathSearch = true,
  normalizePathInput = defaultNormalizePathInput,
  inline = false,
  onNavigate,
  onNavigateUp,
  onRetry,
  children,
  className,
}: DirectoryBrowserProps) {
  const homeTarget = homePath ?? rootPath;
  const canNavigateUp = parentPath != null;
  const [isPathSearchOpen, setIsPathSearchOpen] = React.useState(false);
  const [pathInput, setPathInput] = React.useState('');
  const pathInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isPathSearchOpen) {
      setPathInput(currentPath);
      requestAnimationFrame(() => pathInputRef.current?.focus());
    }
  }, [currentPath, isPathSearchOpen]);

  const openTypedPath = React.useCallback(() => {
    const path = normalizePathInput(pathInput);
    setIsPathSearchOpen(false);
    onNavigate(path);
  }, [normalizePathInput, onNavigate, pathInput]);

  // Toolbar: navigation chips on the left, primary/header actions on
  // the right. Doubles as the breadcrumb strip and the action shelf
  // so commit buttons sit flush against the top of the file list.
  const breadcrumbRow = (
    <div
      className={cn(
        'flex min-w-0 shrink-0 items-center gap-1 border-gray-100 border-b px-3 py-2 dark:border-white/5',
        !inline && 'border-t bg-gray-50/60 dark:bg-white/[0.02]'
      )}
    >
      <SimpleTooltip content="Up one folder" side="bottom">
        <button
          type="button"
          onClick={onNavigateUp}
          disabled={!canNavigateUp}
          aria-label="Go up"
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-200/70 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200"
        >
          <ArrowLeft className="size-3.5" />
        </button>
      </SimpleTooltip>
      {showPathSearch && (
        <SimpleTooltip content="Go to path" side="bottom">
          <button
            type="button"
            onClick={() => setIsPathSearchOpen((prev) => !prev)}
            aria-label="Go to path"
            className={cn(
              'flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-200/70 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200',
              isPathSearchOpen && 'bg-gray-200/70 text-gray-700 dark:bg-white/10 dark:text-gray-200'
            )}
          >
            <Search className="size-3.5" />
          </button>
        </SimpleTooltip>
      )}
      <SimpleTooltip content={rootLabel} side="bottom">
        <button
          type="button"
          onClick={() => onNavigate(homeTarget)}
          disabled={currentPath === homeTarget}
          aria-label={rootLabel}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-200/70 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200"
        >
          <Home className="size-3.5" />
        </button>
      </SimpleTooltip>
      <PathBreadcrumbs
        path={currentPath}
        homePath={homePath}
        rootLabel={rootLabel}
        rootPath={rootPath}
        onJump={onNavigate}
      />
      {(headerActions || primaryAction) && (
        <div className="ml-2 flex shrink-0 items-center gap-1.5">
          {headerActions}
          {primaryAction && (
            <Button
              variant={primaryAction.variant ?? 'default'}
              size="sm"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              className={cn('h-8 shrink-0 gap-1.5 px-3 text-[12.5px]', primaryAction.className)}
            >
              {primaryAction.icon}
              {primaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden', className)}>
      {!inline && (
        // Hero header — the canonical "page title" surface for any
        // file/folder picker. Pure title + subtitle; commit actions
        // live on the right of the toolbar below so they align with
        // the top of the file list. Both ProjectPicker and
        // CodeFileBrowser use this; do not stack a second hero
        // outside the browser.
        <div className="flex min-w-0 shrink-0 flex-col gap-1 px-4 pt-3.5 pb-5">
          <h1 className="font-semibold text-[22px] text-gray-900 leading-tight tracking-tight dark:text-gray-100">
            {title}
          </h1>
          {description && (
            <p className="max-w-md text-[13px] text-gray-500 leading-snug dark:text-gray-400">{description}</p>
          )}
        </div>
      )}

      {breadcrumbRow}

      {isPathSearchOpen && (
        <div className="flex shrink-0 items-center gap-2.5 border-gray-100 border-b bg-gray-50/70 px-3 py-2.5 dark:border-white/5 dark:bg-white/[0.04]">
          <Search className="size-4 shrink-0 text-gray-400 dark:text-gray-500" />
          <input
            ref={pathInputRef}
            type="text"
            value={pathInput}
            onChange={(event) => setPathInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                openTypedPath();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setIsPathSearchOpen(false);
              }
            }}
            placeholder="path/to/folder"
            className="h-8 min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2.5 font-mono text-[12.5px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-400 dark:border-white/10 dark:bg-gray-950/50 dark:text-gray-100 dark:focus:border-gray-500 dark:placeholder:text-gray-500"
          />
          <button
            type="button"
            className="h-7 shrink-0 cursor-pointer rounded-md px-2.5 font-medium text-[12px] text-gray-700 transition-colors hover:bg-gray-200/70 dark:text-gray-300 dark:hover:bg-white/10"
            onClick={openTypedPath}
          >
            Open
          </button>
          <button
            type="button"
            aria-label="Cancel"
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-200"
            onClick={() => setIsPathSearchOpen(false)}
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {error && <ErrorState icon={FolderX} title="Couldn't load this folder" description={error} onRetry={onRetry} />}

        {!error && loading && (
          <div className="flex items-center justify-center py-6">
            <Spinner size="size-5" className="text-gray-400 dark:text-gray-500" />
          </div>
        )}

        {!error && !loading && (
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentPath || 'root'}
                initial={{ opacity: 0, x: navDirection === 'forward' ? 12 : -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: navDirection === 'forward' ? -12 : 12 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="flex min-h-full min-w-0 flex-col pt-1.5 pb-1"
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

export interface DirectoryBrowserRowProps {
  icon: React.ReactNode;
  label: string;
  /** Compact metadata rendered on the same line as the label (e.g. "2d · 12 KB"). */
  meta?: React.ReactNode;
  badge?: React.ReactNode;
  disclosure?: boolean;
  selected?: boolean;
  disabled?: boolean;
  title?: string;
  actions?: React.ReactNode;
  onClick: () => void;
  onDoubleClick?: () => void;
  className?: string;
}

export function DirectoryBrowserRow({
  icon,
  label,
  meta,
  badge,
  disclosure = false,
  selected = false,
  disabled = false,
  title,
  actions,
  onClick,
  onDoubleClick,
  className,
}: DirectoryBrowserRowProps) {
  return (
    <div
      className={cn(
        'group relative flex w-full min-w-0 items-center overflow-hidden transition-colors hover:bg-gray-100 dark:hover:bg-white/5',
        selected && 'bg-gray-100/80 dark:bg-white/[0.06]',
        disabled && 'opacity-50',
        className
      )}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        title={title}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 overflow-hidden px-4 py-1.5 text-left disabled:cursor-not-allowed"
      >
        <span className="flex shrink-0 items-center">{icon}</span>
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate text-[13px] text-gray-800 dark:text-gray-200">{label}</span>
          {meta && <span className="shrink-0 text-[11px] text-gray-400 tabular-nums dark:text-gray-500">{meta}</span>}
          {badge && <span className="flex shrink-0 items-center">{badge}</span>}
        </span>
        {disclosure && (
          <ChevronRight className="size-3.5 shrink-0 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-600" />
        )}
      </button>
      {actions && (
        <div className="absolute right-2 flex items-center gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
          {actions}
        </div>
      )}
    </div>
  );
}

interface PathBreadcrumbsProps {
  path: string;
  homePath?: string | null;
  rootLabel: string;
  rootPath: string;
  onJump: (path: string) => void;
}

function PathBreadcrumbs({ path, homePath, rootLabel, rootPath, onJump }: PathBreadcrumbsProps) {
  const crumbs = React.useMemo(
    () => buildBreadcrumbs(path, homePath, rootLabel, rootPath),
    [path, homePath, rootLabel, rootPath]
  );
  const visibleCrumbs = collapseCrumbs(crumbs);

  if (crumbs.length === 0) {
    return <span className="min-w-0 flex-1 px-2 font-mono text-[11px] text-gray-400">...</span>;
  }

  return (
    <div className="flex min-w-0 flex-1 items-center overflow-hidden px-1 font-mono text-[12px] text-gray-500 leading-none dark:text-gray-400">
      {visibleCrumbs.map((crumb, index) => {
        if (crumb.kind === 'ellipsis') {
          return (
            <React.Fragment key="ellipsis">
              {index > 0 && <ChevronRight className="size-3 shrink-0 text-gray-300 dark:text-gray-600" />}
              <span className="flex shrink-0 items-center px-1.5 py-1 text-gray-400 dark:text-gray-500">...</span>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={crumb.path || 'root'}>
            {index > 0 && <ChevronRight className="size-3 shrink-0 text-gray-300 dark:text-gray-600" />}
            <button
              type="button"
              disabled={crumb.current}
              onClick={() => onJump(crumb.path)}
              title={crumb.label}
              className={cn(
                'flex shrink-0 cursor-pointer items-center whitespace-nowrap rounded px-1.5 py-1',
                crumb.current
                  ? 'cursor-default font-medium text-gray-700 dark:text-gray-200'
                  : 'hover:bg-gray-200/60 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200'
              )}
            >
              {crumb.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

type Crumb = {
  kind: 'path';
  label: string;
  path: string;
  current: boolean;
};

type VisibleCrumb = Crumb | { kind: 'ellipsis' };

function buildBreadcrumbs(
  path: string,
  homePath: string | null | undefined,
  rootLabel: string,
  rootPath: string
): Crumb[] {
  if (!path && !rootPath) {
    return [{ kind: 'path', label: rootLabel, path: rootPath, current: true }];
  }

  const absolute = path.startsWith('/');
  // Treat a listing rooted at the configured ``homePath`` (whether the empty
  // string for relative-rooted Jupyter workspaces, or an absolute host home
  // dir for the agent picker) as "the workspace anchor". Both panes label
  // that anchor with ``rootLabel`` ("Workspace" by default) so the UI is
  // consistent, while still allowing users to navigate above it.
  const usingHome =
    homePath != null &&
    (path === homePath || (homePath === '' && !absolute) || (homePath !== '' && path.startsWith(`${homePath}/`)));
  const basePath = usingHome ? homePath : absolute ? '/' : rootPath;
  const baseLabel = usingHome || !absolute ? rootLabel : '/';
  const relativePath = usingHome
    ? path.slice(homePath.length).replace(/^\//, '')
    : absolute
      ? path.replace(/^\//, '')
      : path.replace(new RegExp(`^${escapeRegExp(rootPath)}/?`), '');
  const parts = relativePath.split('/').filter(Boolean);
  const crumbs: Crumb[] = [{ kind: 'path', label: baseLabel, path: basePath, current: parts.length === 0 }];

  let acc = basePath;
  for (const [index, part] of parts.entries()) {
    acc = joinPath(acc, part);
    crumbs.push({ kind: 'path', label: part, path: acc, current: index === parts.length - 1 });
  }

  return crumbs;
}

function collapseCrumbs(crumbs: Crumb[]): VisibleCrumb[] {
  if (crumbs.length <= 5) return crumbs;
  return [crumbs[0], { kind: 'ellipsis' }, ...crumbs.slice(-3)];
}

function joinPath(base: string, child: string): string {
  if (!base) return child;
  if (base === '/') return `/${child}`;
  return `${base.replace(/\/$/, '')}/${child}`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function defaultNormalizePathInput(path: string): string {
  return path.trim();
}
